import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Divider, Paper, Rating, Stack, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { AnimatedNumber } from "../../../components/UIToolkit/Animations/AnimatedNumber";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import { FONT_SIZE, LETTER_SPACING } from "../../../theme/tokens";
import type { PbjStarThreshold } from "../../../utils/models";
import { brandBorder, brandWash, cardShadow, goldAccent, highBorder, highWash, STAR_EMPTY } from "./colors";
import type { PlanOutcome } from "./planOutcome.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import { ratingFromPoints } from "./stars.utils";

type Props = {
	// The lifted plan outcome (Total PPD Spend, Total Price, points gained, reaches-target),
	// computed once in ScoringAndRecommendations — never recomputed here.
	planOutcome: PlanOutcome;
	// Buffered target points the projected score is measured against (round for display only).
	targetPoints: number;
	// Star bands (BE lookup_stars tiers) — used ONLY to map the already-computed projected points
	// to the star rating the plan achieves (a display lookup, not a recompute of the outcome).
	starThresholds: PbjStarThreshold[];
	// Census + quarter/remaining-day counts drive the Total Price tooltip's math note.
	census: number;
	quarterDays: number;
	daysRemaining: number;
	// "quarter" = sustained-quarter tooltip; "qtd" = catch-up-window tooltip.
	periodMode: PeriodMode;
};

/**
 * The "Plan Summary" card — the bottom-RIGHT cell of the 2×2 prediction grid, beside the
 * Required Actions card. This is the CULMINATION of the flow (adjust levers → Required Actions →
 * THIS is the outcome), so it's the emphasized card — but a CALM one on the light working
 * surface, not a dark stage that competes with the hero. It's distinguished from its neighbors by
 * a brand-tinted header strip, and it carries the page's "final result" focal moment: the plan's
 * projected star rating + the large projected-score number over its target, then a prominent
 * verdict (tokenized success wash when the target is reached, brand wash when still short). Keeps
 * the vertical structure: the Total PPD Spend / Total Price (with the quarter/QTD tooltip) /
 * Points Gained rows, then the projected → target result block. Reads every figure from the
 * already-computed `planOutcome` + `targetPoints` — never recomputes (the projected star rating is
 * just a threshold lookup of the projected points). Returns null for an empty plan (mirrors
 * ActionItems), so the card only appears once the plan has an action.
 */
export const PlanSummaryCard: FC<Props> = ({ planOutcome, targetPoints, starThresholds, census, quarterDays, daysRemaining, periodMode }) => {
	const { t } = useTranslation();
	const theme = useTheme();

	if (!planOutcome.hasPlan) return null;

	const isTargetHit = planOutcome.reachesTarget;
	const projectedRounded = Math.round(planOutcome.projectedPoints);
	const targetRounded = Math.round(targetPoints);
	// The star rating the plan projects to — a pure display lookup of the already-computed
	// projected points against the BE star bands (mirrors the hero's current-stars headline).
	const projectedStars = ratingFromPoints(planOutcome.projectedPoints, starThresholds);
	const totalPriceFormatted = planOutcome.totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	// Verdict ink on the light surface: success green when the target is reached, brand accent
	// when still short (both readable dark-on-light; gold stays reserved for the stars only).
	const verdictInk = isTargetHit ? theme.palette.success.main : theme.palette.primary.main;

	// One label↔value row — the muted label anchored left, the bold value right.
	// `emphasize` renders the value in the brand accent (points gained) instead of default ink.
	const statRow = (label: ReactNode, value: ReactNode, opts?: { emphasize?: boolean; dataTestId?: string }): ReactNode => (
		<Stack direction="row" spacing={2} sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
			<Typography variant="body2" color="text.secondary">
				{label}
			</Typography>
			<Typography
				variant="body2"
				sx={{ fontWeight: 700, color: opts?.emphasize ? theme.palette.primary.main : "text.primary", fontVariantNumeric: "tabular-nums" }}
				data-testid={opts?.dataTestId}
			>
				{value}
			</Typography>
		</Stack>
	);

	return (
		<Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: cardShadow(theme) }} data-testid="pbj-model-plan-summary">
			{/* Brand-tinted header strip — the subtle accent that marks this as the culminating card
			    (the neighboring cards use a neutral `background.default` strip). */}
			<Box sx={{ p: 1.5, px: 2, bgcolor: brandWash(theme), borderBottom: `1px solid ${brandBorder(theme)}` }}>
				<PanelHeader icon={<SummarizeOutlinedIcon />} title={t("pbj_model.action_plan.summary")} />
			</Box>
			<Stack spacing={2} sx={{ p: { xs: 2, md: 2.5 } }}>
				{/* Cost + points stat rows, stacked vertically. */}
				{statRow(t("pbj_model.action_plan.total_spend"), t("pbj_model.recommendations.spend_format", { value: planOutcome.totalPpd.toFixed(2) }))}
				{statRow(
					<InHouseTooltip
						arrow
						placement="top"
						title={
							periodMode === "qtd" && daysRemaining > 0
								? t("pbj_model.action_plan.total_price_tooltip_qtd", { census, daysRemaining })
								: t("pbj_model.action_plan.total_price_tooltip", { census, quarterDays })
						}
					>
						<Box component="span" sx={{ cursor: "help", borderBottom: "1px dotted", borderColor: "text.disabled" }}>
							{t("pbj_model.action_plan.total_price")}
						</Box>
					</InHouseTooltip>,
					t("pbj_model.recommendations.spend_format", { value: totalPriceFormatted }),
					{ dataTestId: "pbj-model-action-total-price" },
				)}
				{statRow(t("pbj_model.action_plan.points_gained"), t("pbj_model.recommendations.impact_points", { count: planOutcome.pointsGained }), {
					emphasize: true,
				})}

				<Divider />

				{/* FOCAL RESULT — the plan's projected star rating + the "final result" score over its
				    target, then the verdict. A COMPACT callout (not a tall panel), washed by whether the
				    target is reached, so it reads as the outcome (success green when hit, brand when short). */}
				<Stack
					spacing={1}
					sx={{
						p: 1.5,
						borderRadius: 2,
						bgcolor: isTargetHit ? highWash(theme) : brandWash(theme),
						border: `1px solid ${isTargetHit ? highBorder(theme) : brandBorder(theme)}`,
					}}
				>
					<Box>
						<Typography
							sx={{ textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, fontWeight: 700, fontSize: FONT_SIZE.LABEL_XS, color: "text.secondary" }}
						>
							{t("pbj_model.gauge.projected_eyebrow", { count: projectedStars })}
						</Typography>
						<Rating
							value={projectedStars}
							readOnly
							sx={{
								mt: 0.25,
								fontSize: "1.0625rem",
								"& .MuiRating-iconFilled": { color: goldAccent(theme) },
								"& .MuiRating-iconEmpty": { color: STAR_EMPTY },
							}}
							data-testid="pbj-model-plan-summary-stars"
						/>
						<Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline", mt: 0.25 }}>
							<Typography
								component="div"
								sx={{
									fontSize: FONT_SIZE.TITLE_XXL,
									fontWeight: 700,
									lineHeight: 1,
									letterSpacing: "-0.02em",
									color: "text.primary",
									fontVariantNumeric: "tabular-nums",
								}}
								data-testid="pbj-model-plan-summary-projected"
							>
								<AnimatedNumber value={projectedRounded} />
							</Typography>
							<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 500, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
								{t("pbj_model.action_plan.of_target", { target: targetRounded })}
							</Typography>
						</Stack>
					</Box>

					{/* VERDICT — prominent, readable dark-on-light. Success ink + check when reached,
					    brand accent + trending-up when still short. */}
					<Stack
						direction="row"
						spacing={1}
						sx={{ alignItems: "center", pt: 1, borderTop: `1px solid ${isTargetHit ? highBorder(theme) : brandBorder(theme)}` }}
					>
						{isTargetHit ? (
							<>
								<CheckCircleRoundedIcon sx={{ fontSize: 20, color: verdictInk }} />
								<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: verdictInk }} data-testid="pbj-model-action-target-hit">
									{t("pbj_model.action_plan.target_hit")}
								</Typography>
							</>
						) : (
							<>
								<TrendingUpIcon sx={{ fontSize: 20, color: verdictInk }} />
								<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: verdictInk }} data-testid="pbj-model-action-need-more">
									{t("pbj_model.action_plan.need_more", { count: planOutcome.remainingPoints })}
								</Typography>
							</>
						)}
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);
};
